import React from 'react';
import PropTypes from 'prop-types';

import { Session as SessionNacre } from '@weezevent/nacre';
import Style from './sessionsDay.module.css';
import { capitalizeFirstLetter } from '../../../attendee/detail/components/utils';
import useAppContext from '../../../../context/useAppContext';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

const Session = React.memo(({ session, locale, selected, onSessionSelected, selectable, setSessionToDelete }) => {
    const [{ event, organization }] = useAppContext();
    const history = useHistory();
    const { t } = useTranslation();

    const title = React.useMemo(() => {
        return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: 'numeric' }).format(moment(session.start_date));
    }, [session, locale]);

    const quotaLabel = React.useMemo(() => {
        if (!session.quota_total) {
            return `${session.quota_taken || 0}`;
        }
        return `${session.quota_taken}/${session.quota_total}`;
    }, [session]);

    return (
        <SessionNacre
            className={Style['session']}
            id={session.uuid}
            title={title}
            subTitle={quotaLabel}
            selectable={selectable}
            checked={selected}
            onChange={({ id, checked }) => onSessionSelected({ uuid: id, checked })}
            status={session.enabled ? 'enabled' : 'disabled'}
            unavailable={session.unavailable}
            onClick={() => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/sessions/${session.uuid}`)}
            tooltip={session.enabled ? t('ticket.sessions.list.sessionActive') : t('ticket.sessions.list.sessionInactive')}
            onDelete={selectable ? null : () => setSessionToDelete([session])}
            onDeleteProps={{
                tooltipText: session.quota_taken > 0 ? t('ticket.sessions.list.tooltip-unavailable') : null,
                disabled: session.quota_taken > 0
            }}
        />
    );
});

const LabelSessionsDay = React.memo(({ date, locale, unavailable }) => {
    const _date = moment(date);

    const weekday = capitalizeFirstLetter(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(_date));
    const day = new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(_date);
    const month = capitalizeFirstLetter(new Intl.DateTimeFormat(locale, { month: 'short' }).format(_date));

    //locale sv-SE for return format YYYY-MM-DD
    const anchor = new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(_date);

    return (
        <div id={anchor} className={Style['label-sessions-day']}>
            <div className={`${Style['label-sessions-day__weekday']} ${unavailable ? Style['label-sessions-day__unavailable'] : ''}`}>{weekday}</div>
            <div className={`${Style['label-sessions-day__day']} ${unavailable ? Style['label-sessions-day__unavailable'] : ''}`}>{day}</div>
            <div className={`${Style['label-sessions-day__month']} ${unavailable ? Style['label-sessions-day__unavailable'] : ''}`}>{month}</div>
        </div>
    );
});

export function SessionsDay({ sessions = [], locale = 'en-GB', selected = [], onSessionSelected = () => {}, selectable = false, setSessionToDelete }) {
    const allSessionsUnavailable = React.useMemo(() => {
        //Check last session only
        return sessions[sessions.length - 1].unavailable;
    }, [sessions]);

    return (
        <div className={Style['sessions-day']}>
            <LabelSessionsDay date={sessions[0].start_date} locale={locale} unavailable={allSessionsUnavailable} />
            <div className={Style['sessions-day__content']}>
                {sessions.map(session => (
                    <Session
                        key={session.uuid}
                        session={session}
                        locale={locale}
                        selected={selected.includes(session.uuid)}
                        onSessionSelected={onSessionSelected}
                        selectable={selectable}
                        setSessionToDelete={setSessionToDelete}
                    />
                ))}
            </div>
        </div>
    );
}

SessionsDay.propTypes = {
    sessions: PropTypes.array,
    locale: PropTypes.string,
    selected: PropTypes.array,
    onSessionSelected: PropTypes.func,
    selectable: PropTypes.bool
};
